import { CommunityTransactionForPurchaseQuery, propertyChangedJoinPointFilter, splitPaymentChecked } from '@skylab-shared/business';
import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { JpkVatCodes, SplitPaymentState } from '@skylab-shared/dataModel';
import { unique } from '@skylab-shared/utils';
import { PurchaseSplitPaymentOnNonDomesticPurchaseError } from '../violations/purchase-split-payment-on-non-domestic-purchase-error';

interface PurchaseModelPart extends ModelBase {
    splitPayment: SplitPaymentState;
    jpkVatGroups: string[];
    communityTransactionId: number;
}

export class PurchaseDocumentJpkGroupsBOPart extends BusinessObjectPart<PurchaseModelPart> {
    public constructor(
        weaver: Weaver
    ) {
        super(weaver);
    }
    public override async initializeAsync(context: BusinessObjectPartContext<PurchaseModelPart>): Promise<void> {
        await super.initializeAsync(context);
        this.registerPropertyChangesRules();
    }

    private registerPropertyChangesRules(): void {
        this.synchronizeSplitPaymentWithJpkGroups();
        this.synchronizeCommunityTransactionWithJPKGroup();
        this.registerSplitPaymentValidationRules();
    }

    private registerSplitPaymentValidationRules(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'splitPayment'], [this.modelType, 'communityTransactionId']],
            [[this.modelType, 'splitPayment']],
            ctx => {
                const { id: zkId } = this.bo.localContext.firstPreloaded(CommunityTransactionForPurchaseQuery, { symbol: 'ZK' });
                return !(splitPaymentChecked(ctx.source.splitPayment) && ctx.source.communityTransactionId !== zkId);
            },
            PurchaseSplitPaymentOnNonDomesticPurchaseError
        );
    }

    private synchronizeCommunityTransactionWithJPKGroup(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['communityTransactionId'])(
            ctx => {
                const { id: zkId } = this.bo.localContext.firstPreloaded(CommunityTransactionForPurchaseQuery, { symbol: 'ZK' });
                if (this.bo.model.communityTransactionId !== zkId) return;
                this.bo.model.jpkVatGroups = this.bo.model.jpkVatGroups.filter(g => g !== JpkVatCodes.IMP);
            }
        )));
    }

    private synchronizeSplitPaymentWithJpkGroups(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['splitPayment'])(
            ctx => {
                this.bo.model.jpkVatGroups = [SplitPaymentState.True, SplitPaymentState.Manual].includes(this.bo.model.splitPayment)
                    ? unique([...this.bo.model.jpkVatGroups, JpkVatCodes.MPP])
                    : this.bo.model.jpkVatGroups.filter(g => g !== JpkVatCodes.MPP);
            }
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['jpkVatGroups'])(
            ctx => {
                this.bo.model.splitPayment = this.bo.model.jpkVatGroups.includes(JpkVatCodes.MPP) ? SplitPaymentState.Manual : SplitPaymentState.False;
            })));
    }
}
